/**
 * NotificationBell — oznamovací centrum schované za user sekcí (Filip 2026-07-24, 2.1).
 *
 * UX kontrakt (Filip):
 *   • ŽÁDNÉ vyskakovací toasty vpravo nahoře — sonner <Toaster> se nemountuje,
 *     toast() volání se jen zachytávají do historie (useSonner čte store i bez
 *     Toasteru).
 *   • Zavřené centrum = jen zvoneček; odznáček (červený pulzující = neodkladné
 *     requires_ack, jantarový = nepřečtená DŮLEŽITÁ warn/error/action) JEN když
 *     něco je. Info/success/systémový šum a zprávy badge NEDRŽÍ.
 *   • Rozbalená lišta: panel se VYSOUVÁ inline nad user blokem (variant
 *     'inline', animace max-height). Minimalizovaná lišta: overlay panel
 *     (variant 'overlay') — do 56px pruhu se inline nevejde.
 *   • Urgent (requires_ack / nová verze UI) panel sám otevře — pokud není mute.
 *   • Mute (nerušit): potlačí auto-otevírání; badge a historie zůstávají.
 *   • Režim „jen důležité“ (default ZAP): odznak jen warn/error/action/ack.
 *     Záložky Důležité|Oprávnění|… jsou seznamový filtr, odznak z nich nečtou.
 *   • Nová verze UI: syntetické action oznámení (Reload/Později) + PERSISTENTNÍ
 *     warn záznam do historie (log_self, guard per build — „poslední oznámení
 *     o nové verzi v něm nebylo").
 *   • Persistence: modul notifications (acm.user_notifications); živý kanál
 *     MQTT `acm/notify/v1/<entity>` pro ACM Link/agenty, UI poll 30 s + focus.
 */
import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useSonner } from 'sonner';
import { api } from '../lib/api';
import { RUNNING_UI_BUILD } from '../lib/build';
import { hardReloadUi } from '../lib/pwa';
import {
  IMPORTANT_ONLY_EVENT,
  isImportantNotification,
  notificationBadge,
  readImportantOnly,
  writeImportantOnly,
} from '../lib/notif-important';
import { notificationText } from '../lib/notification-text';
import { sourceKeyFor } from '../lib/notification-source-keys';
import { isLoggableToastTitle } from '../lib/toast-loggable';
import { History,
  AlertCircle,
  AlertTriangle,
  Bell,
  BellOff,
  BellRing,
  Check,
  CheckCheck,
  Forward,
  Info,
  MessageSquare,
  RefreshCw,
  Send,
  Shield,
  X,
} from 'lucide-react';

export type NotificationLevel = 'info' | 'success' | 'warn' | 'error' | 'action';

interface ServerNotification {
  id: number;
  level: NotificationLevel;
  /** UI sekce: alert | system | comms | approvals. */
  channel?: 'alert' | 'system' | 'comms' | 'approvals';
  stream_slug?: string | null;
  stream_title?: string | null;
  title: string;
  detail: string | null;
  source: string | null;
  metadata?: Record<string, unknown> | null;
  requires_ack: boolean;
  read: boolean;
  created_at: string;
}

// ── Filtr: Důležité | Oprávnění | Systém | Komunikace | Vše
// Důležité = alert sekce warn/error/action (ne toast šum).
// Oprávnění = žádosti (OMA/Link info + ACM-schvalitelné přístupová práva).
// Systém/Komunikace = pojmenované streamy (verze, výpadky, project:…).
const FILTER_KEY = 'acm_notif_filter_v2';
type NotifFilter = 'important' | 'approvals' | 'system' | 'comms' | 'all';

const APPROVAL_KINDS = new Set([
  'oma_proxmox',
  'oma',
  'permission_request',
  'access_request',
  'acm_permission',
  // GitLab žádosti (zapojení workeru, práva k projektu) — Filip 2026-09-13.
  'gitlab_project_request',
  'gitlab_worker_request',
]);

function readFilter(): NotifFilter {
  try {
    const v = localStorage.getItem(FILTER_KEY);
    if (
      v === 'all' || v === 'system' || v === 'comms' ||
      v === 'important' || v === 'approvals'
    ) return v;
  } catch { /* private mode */ }
  return 'important';
}

function notifSection(n: ServerNotification): 'alert' | 'system' | 'comms' | 'approvals' {
  const ch = (n as { channel?: string }).channel;
  if (ch === 'system' || ch === 'comms' || ch === 'alert' || ch === 'approvals') return ch;
  if (isUiBuildRecord(n)) return 'system';
  const kind = (n.metadata as { kind?: string } | null)?.kind;
  if (kind && APPROVAL_KINDS.has(kind)) return 'approvals';
  if (kind === 'acmlink-version' || kind === 'acm-version' || kind === 'outage') return 'system';
  if (kind === 'project-message' || kind === 'dm') return 'comms';
  if (n.source === 'proxmox.oma' || n.source === 'oma' || n.source === 'acm.permission') {
    return 'approvals';
  }
  return 'alert';
}

/**
 * Druhy žádostí, které umí rozhodnout TENHLE panel — pro každý existuje cesta
 * v `decideMut` níž. Seznam je schválně vedle ní: tlačítko, které po kliknutí
 * spadne, je horší než tlačítko schované.
 */
const DECIDABLE_HERE = new Set([
  'permission_request',
  'access_request',
  'acm_permission',
  'authority_contract',
  'gitlab_project_request',
]);

/** Kde se smí schválit — acm_link = v ACM jen info; notification_center = tlačítka. */
function decideSurface(n: ServerNotification): 'acm_link' | 'notification_center' | 'none' {
  const meta = n.metadata as {
    decide_surface?: string;
    kind?: string;
  } | null;
  const raw = String(meta?.decide_surface ?? '').trim();
  if (raw === 'acm_link' || raw === 'notification_center' || raw === 'none') return raw;
  const kind = String(meta?.kind ?? '');
  if (kind === 'oma_proxmox' || kind === 'oma') return 'acm_link';
  if (
    kind === 'permission_request' ||
    kind === 'access_request' ||
    kind === 'acm_permission' ||
    // Žádost GitLab modulu se schvaluje tady. Starší oznámení nesou
    // decide_surface 'gitlab', které nezná nikdo — spadla by na 'acm_link'
    // a zůstala bez tlačítek (Filip 2026-09-13).
    kind === 'gitlab_project_request'
  ) return 'notification_center';
  // Žádost, která neřekla, kde se rozhoduje. Dosud spadla vždy do 'acm_link',
  // takže se v centru ukázala jen věta „Tady jen informace" — i u druhů, které
  // tenhle panel umí rozhodnout (mesh kontrakty, GitLab žádosti). Proto se
  // rozhoduje podle toho, jestli pro daný druh cestu máme.
  if (notifSection(n) === 'approvals') {
    return DECIDABLE_HERE.has(kind) ? 'notification_center' : 'acm_link';
  }
  return 'none';
}

function isAcmApprovable(n: ServerNotification): boolean {
  return decideSurface(n) === 'notification_center' && !n.read;
}

// ── Mute — lokální okamžitě (bez závislosti na auth), ui.prefs jako sync ─────
const MUTE_KEY = 'acm_notif_mute';
const MUTE_EVENT = 'acm-notif-mute-change';

function readMute(): boolean {
  try {
    return localStorage.getItem(MUTE_KEY) === '1';
  } catch {
    return false;
  }
}

function writeMute(v: boolean) {
  try {
    localStorage.setItem(MUTE_KEY, v ? '1' : '0');
  } catch { /* private mode */ }
  window.dispatchEvent(new CustomEvent(MUTE_EVENT));
}

function useMute(): [boolean, (v: boolean) => void] {
  const [mute, setMute] = useState(readMute);
  useEffect(() => {
    const h = () => setMute(readMute());
    window.addEventListener(MUTE_EVENT, h);
    window.addEventListener('storage', h);
    return () => {
      window.removeEventListener(MUTE_EVENT, h);
      window.removeEventListener('storage', h);
    };
  }, []);
  return [mute, (v) => {
    writeMute(v);
    api.mcpSession('ui.prefs_set', { key: 'notifications', value: { mute: v } }, { silent: true })
      .catch(() => {});
  }];
}

function useImportantOnly(): [boolean, (v: boolean) => void] {
  const [on, setOn] = useState(readImportantOnly);
  useEffect(() => {
    const h = () => setOn(readImportantOnly());
    window.addEventListener(IMPORTANT_ONLY_EVENT, h);
    window.addEventListener('storage', h);
    return () => {
      window.removeEventListener(IMPORTANT_ONLY_EVENT, h);
      window.removeEventListener('storage', h);
    };
  }, []);
  return [on, (v) => {
    writeImportantOnly(v);
    setOn(v);
    api.mcpSession(
      'ui.prefs_set',
      { key: 'notifications', value: { important_only: v } },
      { silent: true },
    ).catch(() => {});
  }];
}

// ── Ikony + barvy per level ──────────────────────────────────────────────────
function levelIcon(level: NotificationLevel, size = 12) {
  switch (level) {
    case 'info': return <Info size={size} className='text-blue-400' />;
    case 'success': return <Check size={size} className='text-emerald-400' />;
    case 'warn': return <AlertTriangle size={size} className='text-amber-400' />;
    case 'error': return <AlertCircle size={size} className='text-red-400' />;
    case 'action': return <BellRing size={size} className='text-fuchsia-400' />;
  }
}


function relTime(iso: string, now: number): string {
  const ts = new Date(iso).getTime();
  const s = Math.max(1, Math.round((now - ts) / 1000));
  if (s < 60) return `${s}s`;
  const m = Math.round(s / 60);
  if (m < 60) return `${m}m`;
  const h = Math.round(m / 60);
  if (h < 24) return `${h}h`;
  return `${Math.round(h / 24)}d`;
}

// Záznam „nasazena nová verze" — nové mají metadata.kind, STARÉ (před
// zavedením metadata) se poznají podle source+detail. Bez fallbacku by
// nikdy nezmizely z odznaku (Filip 2026-07-27).
function isUiBuildRecord(n: ServerNotification): boolean {
  if ((n.metadata as { kind?: string } | null)?.kind === 'ui-build') return true;
  return n.source === 'system' && (n.detail ?? '').startsWith('build ');
}

// ── Toast capture — každý sonner toast do persistentní historie ─────────────
// Toaster se NErenderuje (žádné popupy) — sonner store ale toast() volání
// drží a useSonner je vidí, takže historie je kompletní bez zásahu do call-sites.
function useToastCapture(onLogged: () => void) {
  const { toasts } = useSonner();
  const seen = useRef(new Set<string | number>());
  useEffect(() => {
    for (const t of toasts) {
      if (seen.current.has(t.id)) continue;
      seen.current.add(t.id);
      const title = typeof t.title === 'string'
        ? t.title
        : typeof t.description === 'string'
        ? t.description
        : null;
      if (!title) continue; // ReactNode toasty nemají textovou reprezentaci
      // Toast, jehož titulek je jméno nástroje, je identifikátor, ne zpráva —
      // do historie nepatří a přeložit se nedá.
      if (!isLoggableToastTitle(title)) continue;
      const level: NotificationLevel = t.type === 'success'
        ? 'success'
        : t.type === 'error'
        ? 'error'
        : t.type === 'warning'
        ? 'warn'
        : 'info';
      const detail = typeof t.description === 'string' && t.description !== title
        ? t.description
        : undefined;
      api.mcpSession('notifications.log_self', { level, title, detail, source: 'toast' }, { silent: true })
        .then(onLogged)
        .catch(() => {}); // offline/nepřihlášen — toast se prostě nezapíše
    }
    if (seen.current.size > 500) seen.current.clear();
  }, [toasts, onLogged]);
}

// ── Hlavní komponenta ────────────────────────────────────────────────────────
// Panel: floating acm-menu (portal) — stejný chrom jako settings/user popover.
// variant:
//   'inline'  — ikona 14px vedle Plug/Logout v UserBadge
//   'overlay' — rail 40×40 + ikona 20px (sbalená lišta, jako Settings)
export function NotificationBell(
  { variant = 'inline' }: { variant?: 'inline' | 'overlay' | 'phone' },
) {
  const rail = variant === 'overlay';
  const phone = variant === 'phone';
  const { t } = useTranslation();
  const qc = useQueryClient();
  const ref = useRef<HTMLDivElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const [open, setOpen] = useState(false);
  const [mute, setMute] = useMute();
  const [importantOnly, setImportantOnly] = useImportantOnly();
  const [filter, setFilterState] = useState<NotifFilter>(readFilter);
  const [now, setNow] = useState(Date.now());
  const [buildDismissed, setBuildDismissed] = useState(false);
  const [pos, setPos] = useState<
    { top?: number; bottom?: number; left: number; width?: number } | null
  >(null);

  // Compose: zpráva / delegace
  type ComposeMode = null | 'message' | 'delegate';
  const [composeMode, setComposeMode] = useState<ComposeMode>(null);
  const [peerQ, setPeerQ] = useState('');
  const [peerId, setPeerId] = useState('');
  const [composeBody, setComposeBody] = useState('');
  const [composeErr, setComposeErr] = useState<string | null>(null);
  const [delegateCtx, setDelegateCtx] = useState<{
    notification_id: number;
    request_id: string;
    kind: string;
    title: string;
  } | null>(null);

  const closeCompose = () => {
    setComposeMode(null);
    setPeerQ('');
    setPeerId('');
    setComposeBody('');
    setComposeErr(null);
    setDelegateCtx(null);
  };

  const setFilter = (f: NotifFilter) => {
    setFilterState(f);
    try {
      localStorage.setItem(FILTER_KEY, f);
    } catch { /* private mode */ }
  };

  const { data } = useQuery({
    queryKey: ['notifications'],
    queryFn: () =>
      api.mcpSession<{
        ok: boolean;
        items?: ServerNotification[];
        unread?: number;
        unread_action?: number;
        unread_by_channel?: {
          alert?: number;
          system?: number;
          comms?: number;
          approvals?: number;
        };
      }>('notifications.list', { limit: 50 }),
    refetchInterval: 30_000,
    refetchOnWindowFocus: true,
    staleTime: 10_000,
    retry: false,
  });
  const items = (data?.items ?? []).map((n) =>
    n.metadata?.direction === 'out' ? { ...n, read: true } : n
  );
  const unread = {
    total: data?.unread ?? 0,
    requires_ack: data?.unread_action ?? 0,
    system: data?.unread_by_channel?.system ?? 0,
    comms: data?.unread_by_channel?.comms ?? 0,
    approvals: data?.unread_by_channel?.approvals ?? 0,
  };

  // Zvoneček i karta /core/notifications čtou stejná data pod jiným klíčem.
  // Invalidoval se jen zvoneček, takže po rozhodnutí zůstala karta viset na
  // starém stavu, dokud ji někdo neobnovil ručně.
  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ['notifications'] });
    qc.invalidateQueries({ queryKey: ['notifications-history'] });
  };
  useToastCapture(invalidate);

  const ack = useMutation({
    mutationFn: (id: number) => api.mcpSession('notifications.ack', { id }),
    onSettled: invalidate,
  });
  const dismiss = useMutation({
    mutationFn: (id: number) => api.mcpSession('notifications.dismiss', { id }),
    onSettled: invalidate,
  });
  const readAll = useMutation({
    mutationFn: () => api.mcpSession('notifications.read_all', {}),
    onSettled: invalidate,
  });

  // Schválení přímo v ACM (decide_surface=notification_center)
  const decide = useMutation({
    mutationFn: async (p: {
      id: number;
      decision: 'approve' | 'deny';
      request_id: string;
      kind: string;
      /** GitLab žádosti: projekt a připojení, pod kterými se rozhoduje. */
      project?: string;
      connection_id?: string;
    }) => {
      if (p.kind === 'oma_proxmox' || p.kind === 'oma') {
        const r = await api.mcpSession<{ ok?: boolean; error?: string }>(
          'proxmox.request_decide',
          {
            request_id: p.request_id,
            decision: p.decision,
            channel: 'notification_center',
          },
        );
        if (r && r.ok === false) throw new Error(r.error || t('notifications.decideFailed', 'Decide selhalo'));
      } else if (
        p.kind === 'permission_request' ||
        p.kind === 'access_request' ||
        p.kind === 'acm_permission'
      ) {
        const r = await api.mcpSession<{ ok?: boolean; error?: string }>(
          'identity.permission_decide',
          {
            request_id: p.request_id,
            decision: p.decision,
            channel: 'notification_center',
          },
        ).catch(() => null);
        if (r && r.ok === false) {
          throw new Error(
            r.error ||
              t('notifications.permissionNotOnServer', 'Schválení přístupových práv zatím není na serveru (identity.permission_decide).'),
          );
        }
        if (!r) {
          throw new Error(
            t('notifications.permissionNotDeployed', 'Schválení přístupových práv zatím není nasazené (identity.permission_decide).'),
          );
        }
      } else if (p.kind === 'gitlab_project_request') {
        // Žádost vlastní GitLab modul; rozhodnutí znovu ověří živá práva
        // schvalovatele v GitLabu, proto s sebou musí jít i projekt a
        // připojení — bez nich nemá decide_request co otevřít.
        const r = await api.mcpSession<{ ok?: boolean; error?: string; code?: string }>(
          'gitlab.project',
          {
            action: 'decide_request',
            request_id: p.request_id,
            decision: p.decision,
            ...(p.project ? { project: p.project } : {}),
            ...(p.connection_id ? { connection_id: p.connection_id } : {}),
          },
        );
        if (r && r.ok === false) {
          throw new Error(r.error || r.code || t('notifications.decideFailed', 'Decide selhalo'));
        }
      } else if (p.kind === 'authority_contract') {
        // Kontrakty autority (role, FSMO, delegace) — hlas jde do
        // mesh_contracts/castVote; po dosažení hlasů operaci dokončí trigger.
        const r = await api.mcpSession<{ ok?: boolean; error?: string; status?: string }>(
          'mesh.contracts',
          {
            action: p.decision === 'approve' ? 'approve' : 'deny',
            request_id: p.request_id,
          },
        );
        if (r && r.ok === false) {
          throw new Error(r.error || t('notifications.decideFailed', 'Decide selhalo'));
        }
      } else {
        throw new Error(t('notifications.unknownRequestKind', 'Neznámý druh žádosti: {{kind}}', { kind: p.kind || '?' }));
      }
      await api.mcpSession('notifications.ack', { id: p.id });
    },
    onSettled: invalidate,
  });

  const { data: peersData } = useQuery({
    queryKey: ['notif-peers', peerQ],
    queryFn: () =>
      api.mcpSession<{
        ok?: boolean;
        peers?: Array<{ id: string; display: string; label: string; username?: string | null }>;
      }>('notifications.peers', { q: peerQ || undefined, limit: 40 }),
    enabled: !!composeMode,
    staleTime: 15_000,
  });
  const peers = peersData?.peers ?? [];

  const sendMessage = useMutation({
    mutationFn: async () => {
      if (!peerId) throw new Error(t('notifications.pickUser', 'Vyber uživatele'));
      if (!composeBody.trim()) throw new Error(t('notifications.emptyBody', 'Napiš text'));
      const r = await api.mcpSession<{ ok?: boolean; error?: string }>(
        'notifications.message_send',
        { to_entity_id: peerId, body: composeBody.trim() },
      );
      if (r && r.ok === false) throw new Error(r.error || t('notifications.sendFailed', 'Odeslání selhalo'));
    },
    onSuccess: () => {
      closeCompose();
      invalidate();
      setFilter('comms');
    },
    onError: (e: Error) => setComposeErr(e.message),
  });

  const sendDelegate = useMutation({
    mutationFn: async () => {
      if (!peerId) throw new Error(t('notifications.pickUser', 'Vyber uživatele'));
      if (!composeBody.trim()) {
        throw new Error(t('notifications.commentRequired', 'Komentář je povinný'));
      }
      if (!delegateCtx?.request_id) throw new Error(t('notifications.missingRequestId', 'Chybí request_id'));
      const r = await api.mcpSession<{ ok?: boolean; error?: string }>(
        'notifications.approval_delegate',
        {
          to_entity_id: peerId,
          kind: delegateCtx.kind || 'oma_proxmox',
          request_id: delegateCtx.request_id,
          comment: composeBody.trim(),
          notification_id: delegateCtx.notification_id,
          title: delegateCtx.title,
        },
      );
      if (r && r.ok === false) throw new Error(r.error || 'Delegace selhala');
    },
    onSuccess: () => {
      closeCompose();
      invalidate();
      setFilter('approvals');
    },
    onError: (e: Error) => setComposeErr(e.message),
  });

  // Nová verze UI → syntetické oznámení s reload akcí (per-tab) + PERSISTENTNÍ
  // warn záznam do historie (guard per build, ať to nespamují taby/reloady).
  const { data: serverInfo } = useQuery({
    queryKey: ['server-info'],
    queryFn: () =>
      api.mcp<{ env: string; label: string | null; hostname: string; ui_build?: string }>(
        'acm-self.ui_context',
      ),
    staleTime: 60_000,
  });
  const buildMismatch = !!serverInfo?.ui_build && !!RUNNING_UI_BUILD &&
    serverInfo.ui_build !== RUNNING_UI_BUILD;
  const newBuild = buildMismatch && !buildDismissed;

  // Persistentní záznam „nasazena nová verze" — hlídá se ZMĚNA buildu, který
  // tenhle prohlížeč reálně načetl (localStorage acm_last_ui_build). Mismatch
  // ve staré záložce nestačí: kdo reloadne dřív, než effect stihne zapsat,
  // neměl v historii nic (Filip 2026-07-24). Guard per build proti spamu tabů.
  useEffect(() => {
    if (!RUNNING_UI_BUILD) return;
    let last: string | null = null;
    try {
      last = localStorage.getItem('acm_last_ui_build');
      localStorage.setItem('acm_last_ui_build', RUNNING_UI_BUILD);
    } catch {
      return; // private mode — bez persistence nemá guard smysl
    }
    if (!last || last === RUNNING_UI_BUILD) return;
    const guard = `acm_notif_build_${RUNNING_UI_BUILD}`;
    try {
      if (localStorage.getItem(guard)) return;
      localStorage.setItem(guard, '1');
    } catch { /* ignore */ }
    api.mcpSession('notifications.log_self', {
      level: 'warn',
      channel: 'system',
      title: t('notifications.newBuildLogged', 'Nasazena nová verze ACM rozhraní'),
      detail: `build ${last.slice(0, 12)} → ${RUNNING_UI_BUILD.slice(0, 12)}`,
      source: 'system',
      metadata: { kind: 'ui-build', build: RUNNING_UI_BUILD },
    }, { silent: true }).then(invalidate).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Auto-ack zastaralých záznamů o verzi: jsme-li na aktuálním buildu,
  // oznámení „nasazena nová verze" už splnila účel — nemají držet odznak
  // (Filip 2026-07-27: po reloadu nemizely a badge svítil pořád).
  const ackedStale = useRef(new Set<number>());
  useEffect(() => {
    if (buildMismatch) return; // stará záložka — záznamy jsou stále relevantní
    const stale = items.filter((n) => !n.read && isUiBuildRecord(n) && !ackedStale.current.has(n.id));
    if (!stale.length) return;
    stale.forEach((n) => ackedStale.current.add(n.id));
    Promise.allSettled(
      stale.map((n) => api.mcpSession('notifications.ack', { id: n.id }, { silent: true })),
    ).then(invalidate);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [items, buildMismatch]);

  // Urgent auto-otevření (pokud není mute) — jen při NÁRŮSTU čekajících acků.
  const lastAckCount = useRef(0);
  useEffect(() => {
    const pending = unread.requires_ack + (newBuild ? 1 : 0);
    if (pending > lastAckCount.current && !mute) setOpen(true);
    lastAckCount.current = pending;
  }, [unread.requires_ack, newBuild, mute]);

  // Pozice floating panelu vedle zvonečku (vpravo od lišty, ne uvnitř)
  const place = () => {
    const el = ref.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    const gap = 8;
    if (phone) {
      setPos({
        left: 8,
        width: Math.max(200, window.innerWidth - 16),
        top: r.bottom + 6,
      });
      return;
    }
    const spaceBelow = window.innerHeight - r.top;
    const openUp = spaceBelow < 320 && r.bottom > 320;
    const panelW = 352; // w-[22rem]
    setPos({
      left: Math.min(r.right + gap, window.innerWidth - 16 - panelW),
      ...(openUp
        ? { bottom: window.innerHeight - r.bottom }
        : { top: Math.max(8, r.top - 8) }),
    });
  };

  useLayoutEffect(() => {
    if (!open) return;
    place();
    const onScroll = () => place();
    window.addEventListener('resize', place);
    window.addEventListener('scroll', onScroll, true);
    return () => {
      window.removeEventListener('resize', place);
      window.removeEventListener('scroll', onScroll, true);
    };
  }, [open]);

  // Klik mimo panel = zavřít
  useEffect(() => {
    if (!open) return;
    const h = (e: MouseEvent) => {
      const t = e.target as Node;
      if (ref.current?.contains(t)) return;
      if (menuRef.current?.contains(t)) return;
      setOpen(false);
    };
    const key = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', h);
    document.addEventListener('keydown', key);
    return () => {
      document.removeEventListener('mousedown', h);
      document.removeEventListener('keydown', key);
    };
  }, [open]);

  // relativní časy — tick jen když je panel otevřený
  useEffect(() => {
    if (!open) return;
    const i = setInterval(() => setNow(Date.now()), 30_000);
    setNow(Date.now());
    return () => clearInterval(i);
  }, [open]);

  // Urgent = requires_ack (včetně ACM-schvalitelných žádostí), ne Link-only info
  const urgentCount = unread.requires_ack + (newBuild ? 1 : 0);
  const unreadApprovals = items.filter((n) =>
    !n.read && notifSection(n) === 'approvals'
  ).length;
  const unreadImportant = items.filter((n) =>
    !n.read && !n.requires_ack && isImportantNotification(n)
  ).length;
  const unreadAll = items.filter((n) => !n.read).length;
  const badge = notificationBadge({
    importantOnly,
    urgentCount,
    unreadImportant,
    unreadAll,
  });
  const badgeClass = items.some((n) =>
    !n.read && !n.requires_ack && n.level === 'error'
  )
    ? 'bg-red-600 text-white'
    : 'bg-amber-500 text-black';
  const scoped = importantOnly
    ? items.filter((n) => isImportantNotification(n))
    : items;
  const visibleItems = filter === 'all'
    ? scoped
    : filter === 'system'
    ? scoped.filter((n) => notifSection(n) === 'system')
    : filter === 'comms'
    ? scoped.filter((n) => notifSection(n) === 'comms')
    : filter === 'approvals'
    ? scoped.filter((n) => notifSection(n) === 'approvals')
    : scoped.filter((n) => isImportantNotification(n));

  const iconSize = phone ? 15 : rail ? 20 : 14;
  const stroke = rail || phone ? 1.75 : 2;

  const bellButton = (
    <button
      type='button'
      onClick={() => {
        setOpen((v) => {
          if (!v) invalidate();
          return !v;
        });
      }}
      className={
        phone
          ? `phone-header-switch relative ${open ? 'text-gray-100' : ''}`
          : rail
          ? `relative w-10 h-10 rounded-full flex items-center justify-center transition-colors ${
            open
              ? 'bg-gray-800 text-gray-100'
              : 'text-gray-400 hover:text-gray-100 hover:bg-gray-800/50'
          }`
          : `relative p-1.5 rounded-lg transition-colors shrink-0 ${
            open
              ? 'bg-gray-800/80 text-gray-100'
              : 'text-gray-500 hover:text-gray-100 hover:bg-gray-800/50'
          }`
      }
      title={t('notifications.toggle', 'Oznámení')}
      aria-expanded={open}
    >
      {mute
        ? <BellOff size={iconSize} strokeWidth={stroke} />
        : <Bell size={iconSize} strokeWidth={stroke} />}
      {badge.urgent && badge.count > 0 && (
        <span
          className={`absolute rounded-full bg-red-500 text-[9px] font-bold text-white flex items-center justify-center animate-pulse ${
            rail
              ? '-top-0.5 -right-0.5 min-w-[16px] h-4 px-0.5'
              : '-top-0.5 -right-0.5 min-w-[15px] h-[15px] px-0.5'
          }`}
        >
          {badge.count}
        </span>
      )}
      {!badge.urgent && badge.count > 0 && (
        <span
          className={`absolute rounded-full ${badgeClass} text-[9px] font-bold flex items-center justify-center ${
            rail
              ? '-top-0.5 -right-0.5 min-w-[16px] h-4 px-0.5'
              : '-top-0.5 -right-0.5 min-w-[15px] h-[15px] px-0.5'
          }`}
        >
          {badge.count > 99 ? '99+' : badge.count}
        </span>
      )}
    </button>
  );

  // Stejný popover chrom jako CollapsedFooter settings/user (acm-menu, w-60)
  const panel = open && pos && typeof document !== 'undefined'
    ? createPortal(
      <div
        ref={menuRef}
        className={`acm-menu fixed z-[200] flex flex-col rounded-2xl overflow-hidden animate-fade-in shadow-2xl ${
          phone ? 'max-h-[min(36rem,80vh)]' : 'w-[22rem] max-h-[min(32rem,78vh)]'
        }`}
        style={{
          left: pos.left,
          top: pos.top,
          bottom: pos.bottom,
          width: pos.width,
        }}
      >
        <div className='flex flex-col gap-2 px-3 py-2.5 border-b border-gray-800 shrink-0'>
          <div className='flex items-center gap-2'>
            <span className='text-[10px] font-semibold text-gray-500 uppercase tracking-widest flex-1'>
              {t('notifications.title', 'Oznámení')}
            </span>
            <button
              type='button'
              onClick={() => {
                closeCompose();
                setComposeMode('message');
                setComposeErr(null);
              }}
              className={`p-1 rounded-lg transition-colors ${
                composeMode === 'message'
                  ? 'text-sky-300 bg-sky-500/10'
                  : 'text-gray-500 hover:text-sky-300'
              }`}
              title={t('notifications.writeMessage', 'Napsat zprávu')}
            >
              <MessageSquare size={12} />
            </button>
            <button
              type='button'
              onClick={() => setMute(!mute)}
              className={`p-1 rounded-lg transition-colors ${
                mute ? 'text-amber-400' : 'text-gray-500 hover:text-gray-200'
              }`}
              title={mute
                ? t('notifications.muted', 'Ztlumeno')
                : t('notifications.muteHint', 'Ztlumit vyskakovací oznámení (historie zůstává)')}
            >
              {mute ? <BellOff size={12} /> : <Bell size={12} />}
            </button>
            {unread.total > 0 && (
              <button
                type='button'
                onClick={() => readAll.mutate()}
                className='p-1 text-gray-500 hover:text-emerald-400 rounded-lg transition-colors'
                title={t('notifications.readAllHint', 'Označit vše jako přečtené (kromě čekajících akcí)')}
              >
                <CheckCheck size={12} />
              </button>
            )}
            {/* Zvoneček je pomíjivý rychlý povrch — plná dohledatelnost žije
                na kartě /core/notifications (Filip 2026-08-23). */}
            <Link
              to='/core/notifications'
              className='p-1 text-gray-500 hover:text-blue-400 rounded-lg transition-colors'
              title={t('notifications.historyLink', 'Historie oznámení (plná karta)')}
            >
              <History size={12} />
            </Link>
            {/* Zavřít. Panel dosud šel zavřít jen klikem mimo nebo znovu na
                zvoneček — na dotyku i v překryvu to lidi nenašli. */}
            <button
              type='button'
              onClick={() => setOpen(false)}
              className='p-1 text-gray-500 hover:text-white rounded-lg transition-colors'
              title={t('notifications.close', 'Zavřít')}
              aria-label={t('notifications.close', 'Zavřít')}
            >
              <X size={12} />
            </button>
          </div>
          <button
            type='button'
            role='switch'
            aria-checked={importantOnly}
            onClick={() => setImportantOnly(!importantOnly)}
            className={`flex w-full items-center justify-between gap-2 rounded-lg border px-2 py-1.5 text-[11px] transition-colors ${
              importantOnly
                ? 'border-amber-500/40 bg-amber-500/10 text-amber-200'
                : 'border-gray-800 bg-gray-950/40 text-gray-400 hover:text-gray-200'
            }`}
            title={t(
              'notifications.importantOnlyHint',
              'Odznak u zvonku jen pro důležité. Záložky dole filtrují seznam, ne odznak.',
            )}
          >
            <span className='font-medium'>
              {t('notifications.importantOnly', 'jen důležité')}
            </span>
            <span className={`text-[10px] uppercase tracking-wide ${
              importantOnly ? 'text-amber-300' : 'text-gray-600'
            }`}>
              {importantOnly
                ? t('notifications.importantOnlyOn', 'zapnuto')
                : t('notifications.importantOnlyOff', 'vypnuto')}
            </span>
          </button>
          {/* Důležité | Oprávnění | Systém | Komunikace | Vše */}
          <div
            className='inline-flex w-full flex-wrap rounded-lg border border-gray-700/80 bg-gray-950/40 p-0.5 gap-0.5'
            role='group'
            aria-label={t('notifications.filter', 'Filtr oznámení')}
          >
            {([
              ['important', t('notifications.filterImportant', 'Důležité')],
              ['approvals', t('notifications.filterApprovals', 'Oprávnění')],
              ['system', t('notifications.filterSystem', 'Systém')],
              ['comms', t('notifications.filterComms', 'Komunikace')],
              ['all', t('notifications.filterAll', 'Vše')],
            ] as const).map(([id, label]) => (
              <button
                key={id}
                type='button'
                onClick={() => setFilter(id)}
                aria-pressed={filter === id}
                className={`flex-1 min-w-[3.2rem] text-[10px] py-1 px-0.5 rounded-md transition-colors truncate ${
                  filter === id
                    ? 'bg-gray-700 text-gray-100 font-medium'
                    : 'text-gray-500 hover:text-gray-200'
                }`}
              >
                {label}
                {id === 'approvals' && unreadApprovals > 0 ? (
                  <span className='ml-0.5 text-violet-400'>{unreadApprovals}</span>
                ) : null}
              </button>
            ))}
          </div>
        </div>

        <div className='overflow-y-auto flex-1 min-h-0 p-1'>
          {composeMode && (
            <div className='rounded-xl px-2.5 py-2 mb-1.5 border border-sky-500/25 bg-sky-500/5'>
              <div className='flex items-center gap-1.5 mb-1.5'>
                {composeMode === 'delegate'
                  ? <Forward size={12} className='text-violet-400' />
                  : <MessageSquare size={12} className='text-sky-400' />}
                <span className='text-[11px] font-medium text-gray-100 flex-1'>
                  {composeMode === 'delegate'
                    ? t('notifications.delegateTitle', 'Delegovat schválení')
                    : t('notifications.messageTitle', 'Zpráva uživateli')}
                </span>
                <button
                  type='button'
                  onClick={closeCompose}
                  className='p-0.5 text-gray-500 hover:text-gray-200'
                  title={t('notifications.cancel', 'Zrušit')}
                >
                  <X size={12} />
                </button>
              </div>
              {composeMode === 'delegate' && delegateCtx && (
                <p className='text-[10px] text-gray-400 mb-1.5 break-words'>
                  {delegateCtx.title}
                  <span className='text-gray-600 font-mono'> · {delegateCtx.request_id.slice(0, 8)}</span>
                </p>
              )}
              <input
                type='search'
                value={peerQ}
                onChange={(e) => {
                  setPeerQ(e.target.value);
                  setPeerId('');
                }}
                placeholder={t('notifications.searchUser', 'Hledat uživatele…')}
                className='w-full text-[11px] px-2 py-1 rounded-lg bg-gray-950/60 border border-gray-700 text-gray-100 placeholder:text-gray-600 mb-1'
              />
              <div className='max-h-24 overflow-y-auto mb-1.5 space-y-0.5'>
                {peers.length === 0 && (
                  <p className='text-[10px] text-gray-600 px-1 py-1'>
                    {t('notifications.noPeers', 'Nikdo nenalezen')}
                  </p>
                )}
                {peers.map((p) => (
                  <button
                    key={p.id}
                    type='button'
                    onClick={() => setPeerId(p.id)}
                    className={`w-full text-left text-[11px] px-2 py-1 rounded-lg truncate transition-colors ${
                      peerId === p.id
                        ? 'bg-sky-500/20 text-sky-200 border border-sky-500/30'
                        : 'text-gray-300 hover:bg-gray-800/60'
                    }`}
                  >
                    {p.display}
                  </button>
                ))}
              </div>
              <textarea
                value={composeBody}
                onChange={(e) => setComposeBody(e.target.value)}
                rows={composeMode === 'delegate' ? 3 : 2}
                placeholder={
                  composeMode === 'delegate'
                    ? t(
                      'notifications.delegateCommentPh',
                      'Komentář pro delegáta (povinný)…',
                    )
                    : t('notifications.messagePh', 'Text zprávy…')
                }
                className='w-full text-[11px] px-2 py-1.5 rounded-lg bg-gray-950/60 border border-gray-700 text-gray-100 placeholder:text-gray-600 resize-none mb-1.5'
              />
              {composeErr && (
                <p className='text-[10px] text-red-400 mb-1.5'>{composeErr}</p>
              )}
              <button
                type='button'
                disabled={
                  sendMessage.isPending || sendDelegate.isPending ||
                  !peerId || !composeBody.trim()
                }
                onClick={() => {
                  setComposeErr(null);
                  if (composeMode === 'delegate') sendDelegate.mutate();
                  else sendMessage.mutate();
                }}
                className='w-full text-[11px] py-1.5 rounded-lg bg-sky-500/15 border border-sky-500/30 text-sky-200 hover:bg-sky-500/25 transition-colors disabled:opacity-40 inline-flex items-center justify-center gap-1.5'
              >
                <Send size={11} />
                {composeMode === 'delegate'
                  ? t('notifications.delegateSend', 'Delegovat')
                  : t('notifications.messageSend', 'Odeslat')}
              </button>
            </div>
          )}

          {newBuild && (
            <div className='rounded-xl px-2.5 py-2 mb-0.5 bg-fuchsia-500/8 border border-fuchsia-500/20'>
              <div className='flex items-center gap-2'>
                <RefreshCw size={12} className='text-fuchsia-400 flex-shrink-0' />
                <span className='text-xs text-gray-100 flex-1 leading-snug'>
                  {t('notifications.newBuild', 'Nová verze rozhraní je k dispozici')}
                </span>
              </div>
              <div className='flex gap-1.5 mt-1.5 ml-5'>
                <button
                  type='button'
                  onClick={() => void hardReloadUi()}
                  className='text-[10px] px-2 py-0.5 rounded-lg bg-fuchsia-500/15 border border-fuchsia-500/30 text-fuchsia-300 hover:bg-fuchsia-500/25 transition-colors'
                >
                  {t('notifications.reload', 'Znovu načíst')}
                </button>
                <button
                  type='button'
                  onClick={() => setBuildDismissed(true)}
                  className='text-[10px] px-2 py-0.5 rounded-lg text-gray-500 hover:text-gray-200 transition-colors'
                >
                  {t('notifications.later', 'Později')}
                </button>
              </div>
            </div>
          )}

          {visibleItems.length === 0 && !newBuild && (
            <p className='text-[11px] text-gray-500 text-center py-5 px-2'>
              {items.length > 0
                ? importantOnly
                  ? t(
                    'notifications.emptyImportantMode',
                    'Žádná důležitá — režim „jen důležité“ je zapnutý (odznak i seznam).',
                  )
                  : t('notifications.emptyFiltered', 'Žádná důležitá — přepni filtr na Vše')
                : t('notifications.empty', 'Žádná oznámení')}
            </p>
          )}

          {visibleItems.map((n) => {
            const surface = decideSurface(n);
            const section = notifSection(n);
            const meta = (n.metadata ?? {}) as {
              kind?: string;
              request_id?: string;
              project?: string | number;
              connection_id?: string;
              from_entity_id?: string;
              from_label?: string;
              delegation_comment?: string;
              delegated_from_label?: string;
            };
            // Jazyk vybírá čtenář, ne zapisovatel: uložená věta je fallback.
            const text = notificationText(n, t, { sourceKey: sourceKeyFor });
            const acmApprove = isAcmApprovable(n) && !!meta.request_id;
            const linkOnly = section === 'approvals' && surface === 'acm_link';
            const canDelegate = section === 'approvals' && !!meta.request_id && !n.read;
            const canReplyDm = section === 'comms' &&
              (meta.kind === 'dm' || n.source === 'dm') &&
              !!meta.from_entity_id;

            return (
            <div
              key={n.id}
              className={`group acm-menu-item rounded-xl px-2.5 py-2 mb-0.5 transition-colors ${
                n.read ? 'opacity-50' : 'hover:bg-gray-800/50'
              } ${section === 'approvals' && !n.read ? 'border border-violet-500/15 bg-violet-500/5' : ''}`}
            >
              <div className='flex items-start gap-2'>
                <span className='mt-0.5 flex-shrink-0'>
                  {section === 'approvals'
                    ? <Shield size={12} className='text-violet-400' />
                    : section === 'comms' && (meta.kind === 'dm' || n.source === 'dm')
                    ? <MessageSquare size={12} className='text-sky-400' />
                    : levelIcon(n.level)}
                </span>
                <div className='flex-1 min-w-0'>
                  <p className='text-xs text-gray-100 break-words leading-snug'>{text.title}</p>
                  {text.detail && (
                    <p className='text-[10px] text-gray-500 break-words mt-0.5'>{text.detail}</p>
                  )}
                  {meta.delegation_comment && (
                    <p className='text-[10px] text-amber-300/90 mt-1 leading-snug'>
                      {meta.delegated_from_label
                        ? `${meta.delegated_from_label}: `
                        : ''}
                      „{meta.delegation_comment}"
                    </p>
                  )}
                  {linkOnly && !n.read && (
                    <p className='text-[10px] text-violet-300/90 mt-1 leading-snug'>
                      {(meta as { approval_kind?: string }).approval_kind === 'operator_browser' ||
                      meta.kind === 'oma_proxmox' || meta.kind === 'oma'
                        ? t(
                          'notifications.approveOperatorBrowser',
                          'Schválit k provedení v ACMLink — plugin pošle požadavek tvým přihlášením k cíli (prohlížeč). Tady jen informace.',
                        )
                        : t(
                          'notifications.approveInLink',
                          'Schválení v ACMLink. Tady jen informace.',
                        )}
                    </p>
                  )}
                  {section === 'approvals' &&
                    (meta as { approval_kind?: string }).approval_kind === 'platform' &&
                    !n.read &&
                    !acmApprove && (
                    <p className='text-[10px] text-emerald-300/90 mt-1 leading-snug'>
                      {t(
                        'notifications.approvePlatform',
                        'Schválení na platformě ACM (ne browser hop).',
                      )}
                    </p>
                  )}
                  <p className='text-[9px] text-gray-600 mt-0.5'>
                    {n.stream_title && (
                      <span className='text-purple-400/90'>{n.stream_title} · </span>
                    )}
                    {!n.stream_title && n.source && (
                      <span className='font-mono'>{n.source} · </span>
                    )}
                    {relTime(n.created_at, now)}
                  </p>
                  {isUiBuildRecord(n) && (
                    <button
                      type='button'
                      onClick={() => {
                        ack.mutate(n.id);
                        setTimeout(() => void hardReloadUi(), 150);
                      }}
                      className='mt-1 text-[10px] px-2 py-0.5 rounded-lg bg-fuchsia-500/15 border border-fuchsia-500/30 text-fuchsia-300 hover:bg-fuchsia-500/25 transition-colors inline-flex items-center gap-1'
                    >
                      <RefreshCw size={10} /> {t('notifications.reload', 'Znovu načíst')}
                    </button>
                  )}
                  {(acmApprove || canDelegate || canReplyDm) && (
                    <div className='flex gap-1.5 mt-1.5 flex-wrap'>
                      {acmApprove && (
                        <>
                          <button
                            type='button'
                            disabled={decide.isPending}
                            onClick={() =>
                              decide.mutate({
                                id: n.id,
                                decision: 'approve',
                                request_id: String(meta.request_id),
                                kind: String(meta.kind || ''),
                                ...(meta.project != null ? { project: String(meta.project) } : {}),
                                ...(meta.connection_id
                                  ? { connection_id: String(meta.connection_id) }
                                  : {}),
                              })}
                            className='text-[10px] px-2 py-0.5 rounded-lg bg-emerald-500/15 border border-emerald-500/30 text-emerald-300 hover:bg-emerald-500/25 transition-colors disabled:opacity-50'
                          >
                            {t('notifications.approve', 'Schválit')}
                          </button>
                          <button
                            type='button'
                            disabled={decide.isPending}
                            onClick={() =>
                              decide.mutate({
                                id: n.id,
                                decision: 'deny',
                                request_id: String(meta.request_id),
                                kind: String(meta.kind || ''),
                                ...(meta.project != null ? { project: String(meta.project) } : {}),
                                ...(meta.connection_id
                                  ? { connection_id: String(meta.connection_id) }
                                  : {}),
                              })}
                            className='text-[10px] px-2 py-0.5 rounded-lg bg-red-500/15 border border-red-500/30 text-red-300 hover:bg-red-500/25 transition-colors disabled:opacity-50'
                          >
                            {t('notifications.deny', 'Zamítnout')}
                          </button>
                        </>
                      )}
                      {canDelegate && (
                        <button
                          type='button'
                          onClick={() => {
                            setComposeMode('delegate');
                            setDelegateCtx({
                              notification_id: n.id,
                              request_id: String(meta.request_id),
                              kind: String(meta.kind || 'oma_proxmox'),
                              title: n.title,
                            });
                            setComposeBody('');
                            setPeerId('');
                            setPeerQ('');
                            setComposeErr(null);
                          }}
                          className='text-[10px] px-2 py-0.5 rounded-lg bg-violet-500/15 border border-violet-500/30 text-violet-300 hover:bg-violet-500/25 transition-colors inline-flex items-center gap-1'
                        >
                          <Forward size={10} />
                          {t('notifications.delegate', 'Delegovat')}
                        </button>
                      )}
                      {canReplyDm && (
                        <button
                          type='button'
                          onClick={() => {
                            setComposeMode('message');
                            setPeerId(String(meta.from_entity_id));
                            setPeerQ(meta.from_label || '');
                            setComposeBody('');
                            setComposeErr(null);
                            setDelegateCtx(null);
                          }}
                          className='text-[10px] px-2 py-0.5 rounded-lg bg-sky-500/15 border border-sky-500/30 text-sky-300 hover:bg-sky-500/25 transition-colors inline-flex items-center gap-1'
                        >
                          <MessageSquare size={10} />
                          {t('notifications.reply', 'Odpovědět')}
                        </button>
                      )}
                    </div>
                  )}
                </div>
                <div className='flex items-center gap-0.5 flex-shrink-0'>
                  {n.requires_ack && !n.read && !acmApprove
                    ? (
                      <button
                        type='button'
                        onClick={() => ack.mutate(n.id)}
                        className='text-[10px] px-2 py-0.5 rounded-lg bg-fuchsia-500/15 border border-fuchsia-500/30 text-fuchsia-300 hover:bg-fuchsia-500/25 transition-colors'
                      >
                        {t('notifications.ackBtn', 'Ok')}
                      </button>
                    )
                    : (
                      <>
                        {!n.read && !acmApprove && (
                          <button
                            type='button'
                            onClick={() => ack.mutate(n.id)}
                            title={t('notifications.markRead', 'Označit jako přečtené')}
                            className='opacity-0 group-hover:opacity-100 text-gray-500 hover:text-emerald-400 transition-all p-0.5'
                          >
                            <Check size={11} />
                          </button>
                        )}
                        <button
                          type='button'
                          onClick={() => dismiss.mutate(n.id)}
                          title={t('notifications.dismiss', 'Odstranit')}
                          className='opacity-0 group-hover:opacity-100 text-gray-500 hover:text-red-400 transition-all p-0.5'
                        >
                          <X size={11} />
                        </button>
                      </>
                    )}
                </div>
              </div>
            </div>
            );
          })}
        </div>
      </div>,
      document.body,
    )
    : null;

  return (
    <div ref={ref} className='relative shrink-0'>
      {bellButton}
      {panel}
    </div>
  );
}
